import type { EditorState, LexicalEditor } from 'lexical';
import { $getRoot, $createParagraphNode, $createTextNode } from 'lexical';

/**
 * Converts a Lexical editor state to markdown format
 */
export function lexicalToMarkdown(editorState: EditorState): string {
  // TODO: Implement proper conversion
  // For now, return a simple text representation
  return editorState.read(() => {
    return editorState._nodeMap.get('root')?.getTextContent() || '';
  });
}

/**
 * Converts markdown to a Lexical editor state
 */
export function markdownToLexical(markdown: string, editor: LexicalEditor): void {
  // TODO: Implement proper conversion
  // For now, just set the text content
  editor.update(() => {
    const root = $getRoot();
    root.clear();
    const paragraph = $createParagraphNode();
    paragraph.append($createTextNode(markdown));
    root.append(paragraph);
  });
}

/**
 * Helper to get plain text from a Lexical editor state
 */
export function getPlainText(editorState: EditorState): string {
  return editorState.read(() => {
    return editorState._nodeMap.get('root')?.getTextContent() || '';
  });
}

/**
 * Creates an empty Lexical editor state as JSON string
 */
export function createEmptyLexicalState(): string {
  return JSON.stringify({
    root: {
      children: [{
        children: [],
        direction: null,
        format: "",
        indent: 0,
        type: "paragraph",
        version: 1
      }],
      direction: null,
      format: "",
      indent: 0,
      type: "root",
      version: 1
    }
  });
}

/**
 * Creates a Lexical editor state from plain text
 */
export function createLexicalState(text: string): string {
  return JSON.stringify({
    root: {
      children: [{
        children: [{
          detail: 0,
          format: 0,
          mode: "normal",
          style: "",
          text,
          type: "text",
          version: 1
        }],
        direction: null,
        format: "",
        indent: 0,
        type: "paragraph",
        version: 1
      }],
      direction: null,
      format: "",
      indent: 0,
      type: "root",
      version: 1
    }
  });
}

/**
 * Extracts plain text from a Lexical JSON string, preserving line breaks between paragraphs/blocks
 */
export function extractPlainText(lexicalState: string): string {
  try {
    const state = JSON.parse(lexicalState);
    let lines: string[] = [];

    const extractText = (node: any): string => {
      if (node.type === 'text' && typeof node.text === 'string') return node.text;
      // Preserve link-preview node as its URL so downstream systems (e.g. Bluesky embed)
      // can detect and attach external cards when the editor stores a DecoratorNode.
      if (node.type === 'link-preview' && typeof node.url === 'string') return node.url;
      if (node.type === 'linebreak') return '\n';
      if (node.children) return node.children.map(extractText).join('');
      return '';
    };

    if (state.root?.children) {
      lines = state.root.children.map(extractText);
    }

    // Join blocks with newlines (in case of multiple paragraphs)
    return lines.join('\n');
  } catch (e) {
    console.error('Error extracting plain text:', e);
    return '';
  }
} 